import PropTypes from 'prop-types';
import React, { useContext, useMemo } from 'react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Card,
  Skeleton,
  SkeletonItem,
  Subtitle1,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { countries } from '../common/countries';

const useStyles = makeStyles({
  card: {
    marginBottom: '12px',
  },
  field: {
    display: 'table-row',
  },
  fields: {
    borderSpacing: '1em',
    display: 'table',
  },
  label: {
    display: 'table-cell',
    minWidth: '136px',
    whiteSpace: 'nowrap',
  },
  labelContents: {
    columnGap: '5px',
    display: 'flex',
  },
  value: {
    display: 'table-cell',
  },
});

const StudySummaryCardField = ({
  helpTopic,
  label,
  value,
}) => {
  const styles = useStyles();
  return (
    <section className={styles.field}>
      <div className={styles.label}>
        <div className={styles.labelContents}>
          <Text weight="bold">{label}</Text>
          {!!helpTopic && (
            <HelpButton
              aria-label={label}
              topic={helpTopic}
            />
          )}
        </div>
      </div>
      <div className={styles.value}>
        <Text>{value}</Text>
      </div>
    </section>
  );
};

StudySummaryCardField.propTypes = {
  helpTopic: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

StudySummaryCardField.defaultProps = {
  helpTopic: null,
};

export const StudySummaryCard = ({
  campaigns,
  country,
  endDate,
  keywords,
  startDate,
  uetTags,
}) => {
  const styles = useStyles();
  const i18n = useContext(I18nContext);
  const cardTitle = i18n.getString(_TL_('Summary'));
  const campaignsLabel = i18n.getString(_TL_('Campaigns'));
  const campaignsValue = useMemo(() => campaigns.map(campaign => campaign.name).join(i18n.getString(_TL_(', '))), [campaigns, i18n]);
  const countryLabel = i18n.getString(_TL_('Country'));
  const countryValue = i18n.getString(countries[country].name);
  const dateRangeLabel = i18n.getString(_TL_('Experiment dates'));
  const dateRangeValue = i18n.getString(_TL_('{{startDate}} - {{endDate}}'), {
    startDate: i18n.formatDate(new Date(startDate), { raw: 'MM/dd/yy' }),
    endDate: i18n.formatDate(new Date(endDate), { raw: 'MM/dd/yy' }),
  });
  const keywordsLabel = i18n.getString(_TL_('Keywords'));
  const keywordsValue = useMemo(
    () => (
      keywords.length
        ? keywords.join(i18n.getString(_TL_(', ')))
        : i18n.getString(_TL_('None selected'))
    ),
    [keywords, i18n]
  );
  const uetTagsLabel = i18n.getString(_TL_('UET Tags'));
  const uetTagsValue = useMemo(
    () => (
      uetTags.length
        ? uetTags.join(i18n.getString(_TL_(', ')))
        : i18n.getString(_TL_('None selected'))
    ),
    [i18n, uetTags]
  );

  return (
    <div className="study-summary-card">
      <Card className={styles.card}>
        <Subtitle1>{cardTitle}</Subtitle1>
        <section className={styles.fields}>
          <StudySummaryCardField
            helpTopic="pop_BA_Experiments_Campaigns"
            label={campaignsLabel}
            value={campaignsValue}
          />
          <StudySummaryCardField
            helpTopic="pop_BA_Experiments_Country"
            label={countryLabel}
            value={countryValue}
          />
          <StudySummaryCardField
            helpTopic="pop_BA_Experiments_Dates"
            label={dateRangeLabel}
            value={dateRangeValue}
          />
          <StudySummaryCardField
            label={keywordsLabel}
            value={keywordsValue}
          />
          <StudySummaryCardField
            label={uetTagsLabel}
            value={uetTagsValue}
          />
        </section>
      </Card>
    </div>
  );
};

StudySummaryCard.propTypes = {
  campaigns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
  })).isRequired,
  country: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
  startDate: PropTypes.string.isRequired,
  uetTags: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ])).isRequired,
};

const useSkeletonStyles = makeStyles({
  card: {
    marginBottom: '12px',
  },
  firstRow: {
    alignItems: 'center',
    columnGap: '20px',
    display: 'grid',
    gridTemplateColumns: '17% 42%',
    position: 'relative',
    rowGap: '12px',
  },
  row: {
    alignItems: 'center',
    columnGap: '20px',
    display: 'grid',
    gridTemplateColumns: '17% 17%',
    paddingTop: '20px',
    position: 'relative',
    rowGap: '12px',
  },
  skeletonRoot: {
    marginBottom: '12px',
    marginLeft: '12px',
    marginRight: '12px',
    marginTop: '12px',
  },
});

export const StudySummaryCardSkeleton = () => {
  const styles = useSkeletonStyles();
  const i18n = useContext(I18nContext);
  const loadingMessage = i18n.getString(_TL_('Loading content'));
  const cardTitle = i18n.getString(_TL_('Experiment summary'));

  return (
    <Card className={styles.card}>
      <Subtitle1>{cardTitle}</Subtitle1>
      <Skeleton aria-label={loadingMessage} className={styles.skeletonRoot}>
        <div className={styles.firstRow}>
          <SkeletonItem size={20} />
          <SkeletonItem size={20} />
        </div>
        <div className={styles.row}>
          <SkeletonItem size={20} />
          <SkeletonItem size={20} />
        </div>
        <div className={styles.row}>
          <SkeletonItem size={20} />
          <SkeletonItem size={20} />
        </div>
      </Skeleton>
    </Card>
  );
};
